import React, { useState, useRef, useEffect } from 'react';
import { Volume2, VolumeX, Pause, Play, Settings, Loader2 } from 'lucide-react';
import './TTSPlayer.css';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Deepgram Aura-2 voices — natural-sounding generation (less robotic).
const VOICES = {
  andromeda: { name: 'Andromeda', description: 'Warm, natural female (recommended)' },
  luna: { name: 'Luna', description: 'Soft, conversational female' },
  asteria: { name: 'Asteria', description: 'Clear, professional female' },
  athena: { name: 'Athena', description: 'Calm, mature female' },
  orion: { name: 'Orion', description: 'Deep, authoritative male' },
  arcas: { name: 'Arcas', description: 'Natural, conversational male' }
};

const TTSPlayer = ({ 
  slides, 
  currentSlide, 
  onSlideChange,
  guideTitle 
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [voice, setVoice] = useState(() => {
    try {
      const saved = localStorage.getItem('cadence_voice');
      const allowed = ['arcas', 'orion', 'andromeda', 'luna', 'asteria', 'athena'];
      return allowed.includes(saved) ? saved : 'arcas';
    } catch {
      return 'arcas';
    }
  });
  const [showSettings, setShowSettings] = useState(false);
  const [autoAdvance, setAutoAdvance] = useState(true);
  const [error, setError] = useState(null);
  const audioRef = useRef(null);
  const currentSlideRef = useRef(currentSlide);

  // Persist the chosen narration voice so it becomes the default everywhere.
  useEffect(() => {
    try {
      localStorage.setItem('cadence_voice', voice);
    } catch {
      /* ignore */
    }
  }, [voice]);

  // Keep ref in sync with prop
  useEffect(() => {
    currentSlideRef.current = currentSlide;
  }, [currentSlide]);

  // Extract readable text from slide
  const getSlideText = (slide) => {
    if (!slide) return '';
    
    let text = '';
    
    // Add title
    if (slide.title) {
      text += slide.title + '. ';
    }
    
    // Add main content
    if (slide.content) {
      text += slide.content + ' ';
    }
    
    // Add bullet points
    if (slide.bullets && Array.isArray(slide.bullets)) {
      slide.bullets.forEach(bullet => {
        if (typeof bullet === 'string') {
          text += bullet + '. ';
        } else if (bullet.text) {
          text += bullet.text + '. ';
        }
      });
    }
    
    // Add key insight
    if (slide.keyInsight) {
      text += 'Key insight: ' + slide.keyInsight + ' ';
    }
    
    // Add quote
    if (slide.quote) {
      text += slide.quote + ' ';
    }
    
    // Clean up text
    return text
      .replace(/<[^>]*>/g, '') // Remove HTML tags
      .replace(/\s+/g, ' ')    // Normalize whitespace
      .trim();
  };

  // Generate audio for current slide
  const generateAudio = async (slideIndex) => {
    const slide = slides[slideIndex];
    if (!slide) return null;
    
    const text = getSlideText(slide);
    if (!text) return null;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_URL}/api/tts/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          text: text,
          voice: voice,
          speed: 1.0
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to generate audio');
      }
      
      const audioBlob = await response.blob();
      return URL.createObjectURL(audioBlob);
      
    } catch (err) {
      console.error('TTS error:', err);
      setError('Unable to generate audio');
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  // Play current slide
  const playSlide = async () => {
    const audioUrl = await generateAudio(currentSlide);
    if (!audioUrl) return;
    
    if (audioRef.current) {
      audioRef.current.src = audioUrl;
      audioRef.current.play();
      setIsPlaying(true);
    }
  };

  // Handle play/pause toggle
  const togglePlayPause = () => {
    if (isPlaying) {
      if (audioRef.current) {
        audioRef.current.pause();
      }
      setIsPlaying(false);
    } else {
      playSlide();
    }
  };

  // Handle audio ended - auto advance to next slide
  const handleAudioEnded = () => {
    if (autoAdvance && currentSlideRef.current < slides.length - 1) {
      // Move to next slide
      const nextSlide = currentSlideRef.current + 1;
      onSlideChange(nextSlide);
      
      // Play next slide after a brief pause
      setTimeout(() => {
        if (isPlaying) {
          playSlide();
        }
      }, 500);
    } else {
      setIsPlaying(false);
    }
  };

  // Stop playing when slide changes manually
  useEffect(() => {
    if (audioRef.current && isPlaying) {
      // If user manually changed slide while playing, play the new slide
      playSlide();
    }
  }, [currentSlide]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
      }
    };
  }, []);

  return (
    <div className="tts-player" data-testid="tts-player">
      <audio 
        ref={audioRef} 
        onEnded={handleAudioEnded}
        onError={() => setError('Audio playback error')}
      />
      
      <div className="tts-controls">
        {/* Main play/pause button */}
        <button
          className={`tts-play-btn ${isPlaying ? 'playing' : ''}`}
          onClick={togglePlayPause}
          disabled={isLoading}
          title={isPlaying ? 'Pause narration' : 'Listen to this guide'}
          data-testid="tts-play-btn"
        >
          {isLoading ? (
            <Loader2 size={20} className="spin" />
          ) : isPlaying ? (
            <Pause size={20} />
          ) : (
            <Volume2 size={20} />
          )}
          <span className="tts-label">
            {isLoading ? 'Loading...' : isPlaying ? 'Pause' : 'Listen'}
          </span>
        </button>

        {/* Settings button */}
        <button
          className="tts-settings-btn"
          onClick={() => setShowSettings(!showSettings)}
          title="Voice settings"
          data-testid="tts-settings-btn"
        >
          <Settings size={18} />
        </button>
      </div>

      {/* Settings dropdown */}
      {showSettings && (
        <div className="tts-settings-dropdown" data-testid="tts-settings">
          <div className="tts-setting-group">
            <label>Voice</label>
            <div className="voice-options">
              {Object.entries(VOICES).map(([key, { name, description }]) => (
                <button
                  key={key}
                  className={`voice-option ${voice === key ? 'selected' : ''}`}
                  onClick={() => setVoice(key)}
                  title={description}
                >
                  {name}
                  {voice === key && <span className="voice-check">✓</span>}
                </button>
              ))}
            </div>
          </div>
          
          <div className="tts-setting-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={autoAdvance}
                onChange={(e) => setAutoAdvance(e.target.checked)}
              />
              <span>Auto-advance through slides</span>
            </label>
          </div>
        </div>
      )}

      {/* Error message */}
      {error && (
        <div className="tts-error">
          <VolumeX size={16} />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
};

export default TTSPlayer;
